import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';
import { FaPlay, FaPause } from 'react-icons/fa';
import { MdVerified } from 'react-icons/md';
import FollowModal from './FollowModal';
import RankingBattle from './RankingBattle';
import { usePlayer } from './PlayerContext';
import ColorThief from 'colorthief';

const Home = () => {
  const [profile, setProfile] = useState(null);
  const [songs, setSongs] = useState([]);
  const [isFollowModalOpen, setIsFollowModalOpen] = useState(false);
  const [isBattleOpen, setIsBattleOpen] = useState(false);
  const [dynamicColor, setDynamicColor] = useState(null);
  const [hoverColor, setHoverColor] = useState(null);
  const colorCache = useRef({});

  const { playTrack, currentTrack, isPlaying, togglePlay, setPlaylist } = usePlayer();

  // Extract dominant color from image URL
  const extractColor = useCallback(async (imageUrl) => {
    if (!imageUrl) return null;

    // Check cache first
    if (colorCache.current[imageUrl]) {
      return colorCache.current[imageUrl];
    }

    try {
      const img = new Image();
      img.crossOrigin = 'Anonymous';

      return new Promise((resolve) => {
        img.onload = () => {
          try {
            const colorThief = new ColorThief();
            const color = colorThief.getColor(img);
            const hexColor = `#${color.map(c => c.toString(16).padStart(2, '0')).join('')}`;
            colorCache.current[imageUrl] = hexColor;
            resolve(hexColor);
          } catch (e) {
            resolve(null);
          }
        };
        img.onerror = () => resolve(null);
        img.src = imageUrl;
      });
    } catch (e) {
      return null;
    }
  }, []);

  useEffect(() => {
    // Fetch profile
    fetch('/profile.json')
      .then(res => res.json())
      .then(data => setProfile(data))
      .catch(err => console.error('Error loading profile:', err));

    // Fetch songs (data is pre-populated in songs.json)
    fetch('/songs.json')
      .then(res => res.json())
      .then(data => setSongs(data))
      .catch(err => console.error('Error loading songs:', err));
  }, []);

  // Update dynamic color when current track changes
  useEffect(() => {
    if (currentTrack && songs.length > 0) {
      const currentSong = songs.find(s => s.id === currentTrack);
      if (currentSong?.thumbnail) {
        extractColor(currentSong.thumbnail).then(color => {
          if (color) setDynamicColor(color);
        });
      }
    }
  }, [currentTrack, songs, extractColor]);

  // Sync player playlist when songs order changes (e.g., after ranking update)
  useEffect(() => {
    if (songs.length > 0 && currentTrack && songs.some(s => s.id === currentTrack)) {
      const playlistData = songs.map(s => ({
        trackId: s.id,
        title: s.title,
        artist: s.artist,
        thumbnail: s.thumbnail
      }));
      setPlaylist(playlistData);
    }
  }, [songs, currentTrack, setPlaylist]);

  // Handle hover color extraction
  const handleSongHover = useCallback(async (song) => {
    if (song?.thumbnail) {
      const color = await extractColor(song.thumbnail);
      if (color) setHoverColor(color);
    }
  }, [extractColor]);

  const handleSongHoverEnd = useCallback(() => {
    setHoverColor(null);
  }, []);

  const playSong = (song, idx) => {
    // Build playlist for next/prev navigation
    const playlistData = songs.map(s => ({
      trackId: s.id,
      title: s.title,
      artist: s.artist,
      thumbnail: s.thumbnail
    }));
    setPlaylist(playlistData);
    playTrack(song.id, { title: song.title, artist: song.artist, thumbnail: song.thumbnail }, playlistData, idx);
  };

  const playAll = () => {
    if (songs.length > 0) {
      playSong(songs[0], 0);
    }
  };

  const handleTogglePlay = () => {
    if (currentTrack && songs.some(s => s.id === currentTrack)) {
      togglePlay();
    } else {
      playAll();
    }
  };

  if (!profile) return <div className="home-container"><div className="home-header-gradient" style={{ '--hero-color': '#121212' }} /></div>;

  const defaultColor = profile.heroColor || '#5c1b1b';
  // Priority: hover color > dynamic color (from playing song) > default
  const heroColor = hoverColor || dynamicColor || defaultColor;

  // Check if current track is from this playlist
  const isPlayingFromHere = songs.some(s => s.id === currentTrack);

  return (
    <div className="home-container">
      {/* Dynamic Gradient Background */}
      <div className="home-header-gradient" style={{ '--hero-color': heroColor }} />

      <div className="home-content">
        {/* Hero Section */}
        <div className="hero-section">
          {profile.photo ? (
            <img src={profile.photo} alt="Profile" className="hero-image" />
          ) : (
            <div className="hero-image" style={{ background: '#333', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '80px', color: '#aaa' }}>DT</div>
          )}

          <div className="hero-content">
            <div className="verified-badge">
              <MdVerified className="verified-icon" />
              <span>Verified Engineer</span>
            </div>
            <h1 className="hero-title">{profile.name}</h1>
            <p className="hero-subtitle">Handsome, Funny, and Charming SWE</p>
          </div>
        </div>

        {/* Action Bar */}
        <div className="action-bar">
          <div className="action-buttons-group">
            <button className="play-button" aria-label={isPlayingFromHere && isPlaying ? "Pause" : "Play"} onClick={handleTogglePlay}>
              {isPlayingFromHere && isPlaying ? <FaPause size={22} /> : <FaPlay size={22} style={{ marginLeft: '3px' }} />}
            </button>
            <button className="follow-button" onClick={() => setIsFollowModalOpen(true)}>Follow</button>
          </div>
        </div>

        {/* Featured Cards Section */}
        <div className="featured-section">
          <Link to="/projects" className="featured-card featured-projects">
            <span className="featured-icon">💻</span>
            <div className="featured-card-text">
              <h3>View My Projects</h3>
              <p>Check out what I've built</p>
            </div>
          </Link>
          <Link to="/experience" className="featured-card featured-experience">
            <span className="featured-icon">💼</span>
            <div className="featured-card-text">
              <h3>My Experience</h3>
              <p>Where I've worked</p>
            </div>
          </Link>
          <Link to="/education" className="featured-card featured-education">
            <span className="featured-icon">🎓</span>
            <div className="featured-card-text">
              <h3>Education</h3>
              <p>My coursework & degrees</p>
            </div>
          </Link>
        </div>

        {/* Popular Tracks Section */}
        <div className="popular-section">
          <div className="section-header">
            <h2 className="section-title">My Faves</h2>
            <button className="contest-ranking-btn" onClick={() => setIsBattleOpen(true)}>
              Contest My Ranking
            </button>
          </div>

          <div className="tracks-list">
            {songs.map((song, index) => (
              <div
                className={`track-row ${currentTrack === song.id ? 'playing' : ''}`}
                key={song.id}
                onClick={() => playSong(song, index)}
                onMouseEnter={() => handleSongHover(song)}
                onMouseLeave={handleSongHoverEnd}
              >
                <div className="track-index">
                  {currentTrack === song.id && isPlaying ? (
                    <div className="playing-indicator">
                      <span></span><span></span><span></span>
                    </div>
                  ) : (
                    <>
                      <span className="track-index-number">{index + 1}</span>
                      <FaPlay className="track-index-play" size={12} />
                    </>
                  )}
                </div>

                <div className="track-title-col">
                  {song.thumbnail ? (
                    <img src={song.thumbnail} alt="" className="track-img" />
                  ) : (
                    <div className="track-img" style={{ background: '#333' }}></div>
                  )}
                  <div className="track-info-col">
                    <div className={`track-name-main ${currentTrack === song.id ? 'text-green' : ''}`}>
                      {song.title}
                    </div>
                    <div className="track-artist">{song.artist}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* About Section */}
        <div className="about-section">
          <div className="about-listeners">About This Artist</div>
          <p className="about-text">{profile.about}</p>
        </div>
      </div>

      {/* Follow Modal */}
      <FollowModal isOpen={isFollowModalOpen} onClose={() => setIsFollowModalOpen(false)} />

      {/* Ranking Battle Modal */}
      <RankingBattle
        isOpen={isBattleOpen}
        onClose={() => setIsBattleOpen(false)}
        songs={songs}
        onApplyRanking={(newRanking) => setSongs(newRanking)}
      />
    </div>
  );
};

export default Home;
